import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { useSnackbar } from '@/hooks/global-tools';
import { FavoriteChecked } from '@/theme/overrides/CustomIcons';
import TranslationIcon from '@/public/icons/approval/translation.svg';
import LinkIcon from '@/public/icons/approval/link.svg';
import StarIcon from '@/public/icons/approval/star.svg';
import CommentIcon from '@/public/icons/approval/comment.svg';
import DotsVerticalIcon from '@/public/icons/approval/dots-vertical.svg';
import SparkleIcon from './AiAssistantPanel/SparkleIcon';
import { isTranslatedAtom, translationResultAtom } from './atoms';
import { normalizeLang } from '@/utils/approval/i18nText';
import { resolveDepartmentName } from '@/utils/approval/ceoAffiliation';
import { pickLang } from './statusMapping';
import {
  AiAssistantButton,
  CommentCountText,
  CommentIconButton,
  MetaDateIconRow,
  MetaDepartment,
  MetaName,
  MetaRow,
  MetaSep,
  MetaText,
  TitleBlock,
  TitleIconBar,
  TitleIconButton,
  TitleMetaIconRow,
  TitleText,
  TranslateBadge,
} from './styles';
import TranslationMenu from './TranslationMenu';
import { TranslationIconAnchor } from './TranslationMenu/styles';
import ActionMenu from './ActionMenu';
import { ActionMenuAnchor } from './ActionMenu/styles';
import type { ApprovalViewAuthor } from './types';

/** 제목 + 메타(작성자/직급/부서/날짜) + 우측 아이콘 그룹 */
type TitleMetaBarProps = {
  subject: string;
  author: ApprovalViewAuthor;
  authoredAt: string;
  documentId: string; // 액션 메뉴 "링크 복사" 가 조립할 doc URL 의 식별자
  printableRef: React.RefObject<HTMLElement | null>; // 액션 메뉴 "인쇄" 가 전달할 인쇄 대상 ref
  onRequestTranslation: (from: string | null, to: string | null) => void; // 번역 요청 시 from/to 언어 코드 (예: "ko", "en") 전달. 둘 중 하나라도 null 이면 번역 취소로 간주.
  onScrollToComments?: () => void; // 코멘트 아이콘 클릭 시 부모(ApprovalView)의 CommentSlot 영역으로 스크롤
  commentCount?: number; // 코멘트 아이콘 옆 카운트 표시. 0 이상이면 숫자, 0 이면 빈 배지, undefined/null 이면 배지 미노출.
  noBottomBorder?: boolean; // 하단 보더 비활성. 기본 false / divider 를 대체하는 경우 true
  canRecall?: boolean; // 더보기 메뉴 "회수" 항목 노출 여부. 부모(ApprovalView)가 작성자/상태/결재자 진행 여부/양식 코드 조합으로 미리 계산해 전달.
  onRecall?: () => void; // 더보기 메뉴 "회수" 클릭 시 회수 사유 입력 모달을 여는 핸들러
  canRequestReturn?: boolean; // 더보기 메뉴 "반려요청" 항목 노출 여부. 회수와 배타적으로 부모(ApprovalView)가 판정해 전달.
  onRequestReturn?: () => void; // 더보기 메뉴 "반려요청" 클릭 시 사유 입력 모달을 여는 핸들러
  canAddReferrer?: boolean; // 더보기 메뉴 "참조인 추가" 항목 노출 여부 (부모가 security/status 로 판정)
  onAddReferrer?: () => void; // 더보기 메뉴 "참조인 추가" 클릭 시 참조인 추가 모달을 여는 핸들러
  canEditDocument?: boolean; // 더보기 메뉴 "편집" 항목 노출 여부 (부모가 status S·R + 작성자 본인으로 판정)
  onEditDocument?: () => void; // 더보기 메뉴 "편집" 클릭 시 편집 화면 전환 핸들러
  canDeleteDocument?: boolean; // 더보기 메뉴 "삭제" 항목 노출 여부 (편집과 동일 조건)
  onDeleteDocument?: () => void; // 더보기 메뉴 "삭제" 클릭 시 삭제 확인 다이얼로그를 여는 핸들러
  canCancelAttendance?: boolean; // 더보기 메뉴 "품의 취소" 항목 노출 여부 (근태 자가결재, 부모가 판정)
  cancelAttendanceDisabled?: boolean; // "품의 취소" 비활성(취소 불가 품의)
  onCancelAttendance?: () => void;
  canSendCardCompanyMail?: boolean; // 더보기 메뉴 "카드사 메일 발송" 항목 노출 여부 (법인카드 품의 P·C + 발송 권한, 부모가 판정)
  sendCardCompanyMailDisabled?: boolean;
  onSendCardCompanyMail?: () => void;
  isBookmarked: boolean;
  onToggleBookmark: () => void | Promise<void>;
  canOpenAiAssistant?: boolean; // 「AI 어시스턴트」 버튼 노출 (플래그 + 네이티브 v2 타입, 부모가 판정)
  isAiAssistantOpen?: boolean;
  onToggleAiAssistant?: () => void;
};

const formatDate = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const TitleMetaBar = ({
  subject,
  author,
  authoredAt,
  documentId,
  printableRef,
  onRequestTranslation,
  onScrollToComments,
  commentCount,
  noBottomBorder,
  canRecall = false,
  onRecall,
  canRequestReturn = false,
  onRequestReturn,
  canAddReferrer = false,
  onAddReferrer,
  canEditDocument = false,
  onEditDocument,
  canDeleteDocument = false,
  onDeleteDocument,
  canCancelAttendance = false,
  cancelAttendanceDisabled = false,
  onCancelAttendance,
  canSendCardCompanyMail = false,
  sendCardCompanyMailDisabled = false,
  onSendCardCompanyMail,
  isBookmarked,
  onToggleBookmark,
  canOpenAiAssistant = false,
  isAiAssistantOpen = false,
  onToggleAiAssistant,
}: TitleMetaBarProps) => {
  const { t, i18n } = useTranslation();
  const lang = useMemo(() => normalizeLang(i18n.language), [i18n.language]);
  const { showSuccess, showError } = useSnackbar();

  // 공유 아이콘 — viewer URL clipboard 복사. 더보기 메뉴 "링크 복사" 와 동일 동작.
  const handleCopyLink = useCallback(async () => {
    const url = `${window.location.origin}/approval/viewer?doc=${documentId}`;
    try {
      await navigator.clipboard.writeText(url);
      showSuccess({ message: t('링크가 복사되었습니다.') });
    } catch {
      showError({ message: t('링크 복사에 실패했습니다.') });
    }
  }, [documentId, showError, showSuccess, t]);

  const isTranslated = useAtomValue(isTranslatedAtom);
  const translationResult = useAtomValue(translationResultAtom);
  const displayedSubject =
    isTranslated && translationResult
      ? translationResult.translatedSubject
      : subject;

  const name = pickLang(author.name, lang);
  const position = pickLang(author.positionName, lang);
  const department = resolveDepartmentName(author, author.departmentName) ?? '';
  const dateText = formatDate(authoredAt);

  // 번역 팝업 토글. 1단계: depth1 표시만. depth2/실제 번역은 후속 단계.
  const translateBtnRef = useRef<HTMLButtonElement | null>(null);
  const [isTranslateMenuOpen, setIsTranslateMenuOpen] = useState(false);
  const handleTranslateToggle = useCallback(() => {
    setIsTranslateMenuOpen((prev) => !prev);
  }, []);
  const handleTranslateClose = useCallback(() => {
    setIsTranslateMenuOpen(false);
  }, []);

  // 더보기 액션 메뉴 토글. 클릭 시 링크 복사/인쇄/참조인 추가/회수/편집/삭제 액션 노출.
  const moreBtnRef = useRef<HTMLButtonElement | null>(null);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const handleActionMenuToggle = useCallback(() => {
    setIsActionMenuOpen((prev) => !prev);
  }, []);
  const handleActionMenuClose = useCallback(() => {
    setIsActionMenuOpen(false);
  }, []);

  // 메타 형식: [이름 직급 (bold)] [부서] | [날짜]. MO 는 날짜부터 아래 줄 (styles 참고)
  const namePositionText = [name, position].filter(Boolean).join(' ');

  return (
    <TitleBlock noBorder={noBottomBorder}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {isTranslated && (
          <div>
            <TranslateBadge>
              <TranslationIcon width={16} height={16} />
              {t('AI 번역됨')}
            </TranslateBadge>
          </div>
        )}
        <TitleText>{displayedSubject}</TitleText>
      </div>
      <TitleMetaIconRow>
        <MetaRow>
          {namePositionText && <MetaName>{namePositionText}</MetaName>}
          {department && (
            <MetaDepartment title={department}>{department}</MetaDepartment>
          )}
        </MetaRow>
        <MetaDateIconRow>
          {dateText && (
            <>
              <MetaSep>|</MetaSep>
              <MetaText>{dateText}</MetaText>
            </>
          )}
          <TitleIconBar>
            {canOpenAiAssistant && (
              <AiAssistantButton
                type="button"
                aria-label={t('AI 어시스턴트')}
                aria-pressed={isAiAssistantOpen}
                marked={isAiAssistantOpen}
                onClick={onToggleAiAssistant}
              >
                <SparkleIcon />
                <span>{t('AI 어시스턴트')} (beta)</span>
              </AiAssistantButton>
            )}
            <TranslationIconAnchor>
              <TitleIconButton
                ref={translateBtnRef}
                type="button"
                aria-label={t('번역')}
                aria-haspopup="menu"
                aria-expanded={isTranslateMenuOpen}
                marked={isTranslated}
                onClick={handleTranslateToggle}
              >
                <TranslationIcon width={18} height={18} />
              </TitleIconButton>
              <TranslationMenu
                open={isTranslateMenuOpen}
                onClose={handleTranslateClose}
                anchorRef={translateBtnRef}
                onRequestTranslation={onRequestTranslation}
              />
            </TranslationIconAnchor>
            <TitleIconButton
              type="button"
              aria-label={t('링크 복사')}
              onClick={handleCopyLink}
            >
              <LinkIcon width={18} height={18} />
            </TitleIconButton>
            <CommentIconButton
              type="button"
              aria-label={t('코멘트')}
              onClick={onScrollToComments}
            >
              <CommentIcon width={18} height={18} />
              <CommentCountText>{commentCount ?? 0}</CommentCountText>
            </CommentIconButton>
            <TitleIconButton
              type="button"
              aria-label={t('즐겨찾기')}
              onClick={onToggleBookmark}
              title={t('즐겨찾기')}
            >
              {isBookmarked ? (
                <FavoriteChecked width={18} height={18} />
              ) : (
                <StarIcon width={18} height={18} />
              )}
            </TitleIconButton>
            <ActionMenuAnchor>
              <TitleIconButton
                ref={moreBtnRef}
                type="button"
                aria-label={t('더보기')}
                aria-haspopup="menu"
                aria-expanded={isActionMenuOpen}
                onClick={handleActionMenuToggle}
              >
                <DotsVerticalIcon width={18} height={18} />
              </TitleIconButton>
              <ActionMenu
                open={isActionMenuOpen}
                onClose={handleActionMenuClose}
                anchorRef={moreBtnRef}
                documentId={documentId}
                printableRef={printableRef}
                canRecall={canRecall}
                onRecall={onRecall}
                canRequestReturn={canRequestReturn}
                onRequestReturn={onRequestReturn}
                canAddReferrer={canAddReferrer}
                onAddReferrer={onAddReferrer}
                canEdit={canEditDocument}
                onEdit={onEditDocument}
                canDelete={canDeleteDocument}
                onDelete={onDeleteDocument}
                canCancelAttendance={canCancelAttendance}
                cancelAttendanceDisabled={cancelAttendanceDisabled}
                onCancelAttendance={onCancelAttendance}
                canSendCardCompanyMail={canSendCardCompanyMail}
                sendCardCompanyMailDisabled={sendCardCompanyMailDisabled}
                onSendCardCompanyMail={onSendCardCompanyMail}
              />
            </ActionMenuAnchor>
          </TitleIconBar>
        </MetaDateIconRow>
      </TitleMetaIconRow>
    </TitleBlock>
  );
};

export default TitleMetaBar;
